'use client';

import React from 'react';
import {
  BarChart3,
  Car,
  Code2,
  HelpCircle,
  Image as ImageIcon,
  LayoutTemplate,
  ListOrdered,
  Mail,
  MapPin,
  Megaphone,
  PanelBottom,
  Sparkles,
  Star,
  CreditCard,
} from 'lucide-react';
import type { SectionCategory, SectionInstance, SectionStyle } from './section-templates';
import { DEFAULT_SECTION_STYLE, createSectionId } from './section-templates';
import type { PageDesignTokens, PageSettings } from './page-settings';
import { HEADER_VARIATIONS } from '@/components/admin/sections/HeaderSections';
import { HERO_VARIATIONS } from '@/components/admin/sections/HeroSections';
import { FLEET_VARIATIONS } from '@/components/admin/sections/FleetSections';
import { CONTACT_VARIATIONS } from '@/components/admin/sections/ContactSections';
import {
  FAQ_VARIATIONS,
  GALLERY_VARIATIONS,
  LOCATION_VARIATIONS,
  STATS_VARIATIONS,
  STEPS_VARIATIONS,
} from '@/components/admin/sections/StaticSections';
import { FEATURE_VARIATIONS } from '@/components/admin/sections/FeaturesSections';
import { TESTIMONIAL_VARIATIONS } from '@/components/admin/sections/TestimonialsSections';
import { PRICING_VARIATIONS } from '@/components/admin/sections/PricingSections';
import { CTA_VARIATIONS } from '@/components/admin/sections/CTASections';
import { FOOTER_VARIATIONS } from '@/components/admin/sections/FooterSections';
import { CUSTOM_VARIATIONS } from '@/components/admin/sections/CustomSections';
import { applyAgencyBrandToContent, type AgencyBrandProfile } from './agency-brand';

// ──────────────────────────────────────────────────────────────
// Field definitions powering the settings panel
// ──────────────────────────────────────────────────────────────

export type FieldType = 'text' | 'textarea' | 'image' | 'color' | 'number' | 'boolean' | 'select' | 'repeater';

export interface RepeaterField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'image' | 'color' | 'number' | 'boolean';
}

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  placeholder?: string;
  hint?: string;
  itemFields?: RepeaterField[];
  /** For repeater arrays of plain strings */
  stringItems?: boolean;
  itemLabel?: string;
  max?: number;
  min?: number;
}

export interface VariationDef {
  id: string;
  name: string;
  description: string;
  component: React.ComponentType<any>;
  defaultContent: Record<string, any>;
  fields: FieldDef[];
}

export interface CategoryDef {
  id: SectionCategory;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  variations: VariationDef[];
}

// ──────────────────────────────────────────────────────────────
// Reusable field sets
// ──────────────────────────────────────────────────────────────

const CTA_FIELDS: FieldDef[] = [
  { key: 'ctaText', label: 'Button label', type: 'text' },
  { key: 'ctaHref', label: 'Button link', type: 'text', placeholder: '/vehicles' },
];

const FLEET_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  {
    key: 'category',
    label: 'Vehicle category',
    type: 'select',
    options: [
      { value: 'All', label: 'All vehicles' },
      { value: 'Economy', label: 'Economy' },
      { value: 'Compact', label: 'Compact' },
      { value: 'SUV', label: 'SUV' },
      { value: 'Luxury', label: 'Luxury' },
      { value: 'Convertible', label: 'Convertible' },
      { value: 'Electric', label: 'Electric' },
      { value: 'Van', label: 'Van' },
    ],
  },
  { key: 'limit', label: 'Maximum vehicles', type: 'number', min: 1, max: 12 },
  {
    key: 'columns',
    label: 'Columns',
    type: 'select',
    options: [
      { value: '2', label: '2 columns' },
      { value: '3', label: '3 columns' },
      { value: '4', label: '4 columns' },
    ],
  },
  { key: 'showPrices', label: 'Show daily prices', type: 'boolean' },
  { key: 'showRating', label: 'Show rating', type: 'boolean' },
  ...CTA_FIELDS,
  { key: 'emptyText', label: 'Text when the fleet is empty', type: 'text' },
];

const CONTACT_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'whatsapp', label: 'WhatsApp number', type: 'text' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'hours', label: 'Opening hours', type: 'text' },
  { key: 'formTitle', label: 'Form title', type: 'text' },
  { key: 'formNote', label: 'Form note', type: 'text' },
  { key: 'submitText', label: 'Submit button label', type: 'text' },
  { key: 'successMessage', label: 'Success message', type: 'textarea' },
  { key: 'showPhone', label: 'Show phone', type: 'boolean' },
  { key: 'showEmail', label: 'Show email', type: 'boolean' },
  { key: 'showWhatsapp', label: 'Show WhatsApp', type: 'boolean' },
  { key: 'showAddress', label: 'Show address', type: 'boolean' },
  { key: 'source', label: 'Lead source label', type: 'text', hint: 'Used internally to group leads.' },
];

const CONTACT_DEFAULT = {
  eyebrow: 'Contact',
  headline: 'Talk to a human',
  subheadline: 'Questions about a car, a booking or a long-term rental? Send a message and we will get back to you.',
  // Empty contact fields fall back to the live agency profile on the public site.
  phone: '',
  email: '',
  whatsapp: '',
  address: '',
  hours: '',
  formTitle: 'Send a message',
  formNote: 'We reply within one business day.',
  submitText: 'Send message',
  successMessage: 'Thanks — your message is on its way. We will reply shortly.',
  showPhone: true,
  showEmail: true,
  showWhatsapp: true,
  showAddress: true,
  source: 'website',
};

const FAQ_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  {
    key: 'items',
    label: 'Questions',
    type: 'repeater',
    itemLabel: 'Question',
    itemFields: [
      { key: 'question', label: 'Question', type: 'text' },
      { key: 'answer', label: 'Answer', type: 'textarea' },
    ],
  },
  ...CTA_FIELDS,
];

const FAQ_DEFAULT = {
  eyebrow: 'FAQ',
  headline: 'Questions, answered',
  subheadline: 'The things customers ask before they book.',
  items: [
    { question: 'What documents do I need?', answer: 'A valid driving licence, passport or national ID, and a credit card for the deposit.' },
    { question: 'Is the deposit blocked or charged?', answer: 'We pre-authorise the deposit on your card and release it after the car is returned.' },
    { question: 'Can I pick up at the airport?', answer: 'Yes — add airport pick-up when you book and we will meet you at arrivals.' },
    { question: 'What happens if I cancel?', answer: 'Free cancellation up to 24 hours before pick-up. Later cancellations may incur one day’s rental.' },
  ],
  ctaText: 'Still have a question?',
  ctaHref: '/contact',
};

const GALLERY_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  {
    key: 'images',
    label: 'Images',
    type: 'repeater',
    itemLabel: 'Image',
    itemFields: [
      { key: 'image', label: 'Image', type: 'image' },
      { key: 'caption', label: 'Caption', type: 'text' },
    ],
  },
  {
    key: 'columns',
    label: 'Columns',
    type: 'select',
    options: [
      { value: '2', label: '2 columns' },
      { value: '3', label: '3 columns' },
      { value: '4', label: '4 columns' },
    ],
  },
];

const GALLERY_DEFAULT = {
  eyebrow: 'Gallery',
  headline: 'A closer look',
  subheadline: 'The cars, the coast and the people behind the counter.',
  images: [
    { image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80', caption: 'Atlantic coast drives' },
    { image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1200&q=80', caption: 'The fleet' },
    { image: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1200&q=80', caption: 'Ready for pick-up' },
    { image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=80', caption: 'Road trip ready' },
    { image: 'https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=1200&q=80', caption: 'City to surf' },
    { image: 'https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?auto=format&fit=crop&w=1200&q=80', caption: 'Every detail checked' },
  ],
  columns: 3,
};

const STEP_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  {
    key: 'steps',
    label: 'Steps',
    type: 'repeater',
    itemLabel: 'Step',
    itemFields: [
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'text', label: 'Text', type: 'textarea' },
    ],
  },
  ...CTA_FIELDS,
];

const STEPS_DEFAULT = {
  eyebrow: 'How it works',
  headline: 'Four steps to the road',
  subheadline: 'No queues, no surprise fees, no paperwork mountain.',
  steps: [
    { title: 'Choose your car', text: 'Browse the live fleet and pick the category that fits your trip.' },
    { title: 'Pick your dates', text: 'See the real daily rate and availability before you commit.' },
    { title: 'Confirm online', text: 'Add your details, choose protection and pay a small deposit.' },
    { title: 'Pick up the keys', text: 'Meet us at the desk or the airport. We check the car together and you drive away.' },
  ],
  ctaText: 'Start a booking',
  ctaHref: '/vehicles',
};

const STAT_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  {
    key: 'stats',
    label: 'Stats',
    type: 'repeater',
    itemLabel: 'Stat',
    itemFields: [
      { key: 'value', label: 'Value', type: 'text' },
      { key: 'label', label: 'Label', type: 'text' },
    ],
  },
  { key: 'note', label: 'Small note', type: 'text' },
];

const STATS_DEFAULT = {
  eyebrow: 'By the numbers',
  headline: 'A fleet you can count on',
  subheadline: '',
  stats: [
    { value: '50+', label: 'Vehicles' },
    { value: '4.9/5', label: 'Average rating' },
    { value: '10k+', label: 'Happy customers' },
    { value: '24/7', label: 'Support' },
  ],
  note: 'Trusted by travellers along the Atlantic coast.',
};

const LOCATION_FIELDS: FieldDef[] = [
  { key: 'eyebrow', label: 'Small label above title', type: 'text' },
  { key: 'headline', label: 'Headline', type: 'textarea' },
  { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'hours', label: 'Opening hours', type: 'text' },
  { key: 'mapEmbedUrl', label: 'Map embed URL', type: 'text', hint: 'Paste an OpenStreetMap embed URL.' },
  { key: 'directionsUrl', label: 'Directions link', type: 'text' },
  { key: 'showMap', label: 'Show map', type: 'boolean' },
];

const LOCATION_DEFAULT = {
  eyebrow: 'Find us',
  headline: 'Pick up in the heart of the city',
  subheadline: 'Free airport meet-and-greet on request.',
  // Contact fields stay empty so the live agency profile fills them.
  address: '',
  phone: '',
  email: '',
  hours: '',
  mapEmbedUrl: 'https://www.openstreetmap.org/export/embed.html?bbox=-9.62%2C30.39%2C-9.55%2C30.44&layer=mapnik&marker=30.4202%2C-9.5982',
  directionsUrl: 'https://www.openstreetmap.org/?mlat=30.4202&mlon=-9.5982#map=14/30.4202/-9.5982',
  showMap: true,
};

const NAV_FIELDS: FieldDef[] = [
  {
    key: 'navItems',
    label: 'Navigation links',
    type: 'repeater',
    itemLabel: 'Link',
    itemFields: [
      { key: 'label', label: 'Label', type: 'text' },
      { key: 'href', label: 'Link', type: 'text' },
    ],
  },
];

// ──────────────────────────────────────────────────────────────
// Library
// ──────────────────────────────────────────────────────────────

export const SECTION_LIBRARY: CategoryDef[] = [
  {
    id: 'header',
    label: 'Header',
    description: 'Navigation bar at the top of the page',
    icon: LayoutTemplate,
    variations: [
      {
        id: 'classic',
        name: 'Classic',
        description: 'Logo left, menu centre, phone and CTA right.',
        component: HEADER_VARIATIONS.classic,
        defaultContent: {
          logoText: '',
          logoUrl: '',
          navItems: [
            { label: 'Vehicles', href: '/vehicles' },
            { label: 'How it Works', href: '/services' },
            { label: 'Pricing', href: '/deals' },
            { label: 'Contact', href: '/contact' },
          ],
          ctaText: 'Book Now',
          ctaHref: '/vehicles',
          showContact: true,
          phone: '',
          email: '',
        },
        fields: [
          { key: 'logoText', label: 'Logo text', type: 'text' },
          { key: 'logoUrl', label: 'Logo image URL', type: 'image' },
          ...NAV_FIELDS,
          ...CTA_FIELDS,
          { key: 'showContact', label: 'Show phone and email', type: 'boolean' },
          { key: 'phone', label: 'Phone', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
        ],
      },
      {
        id: 'modern',
        name: 'Modern centred',
        description: 'Menu centred, dark CTA on the right.',
        component: HEADER_VARIATIONS.modern,
        defaultContent: {
          logoText: '',
          navItems: [
            { label: 'Fleet', href: '/vehicles' },
            { label: 'Features', href: '/services' },
            { label: 'Testimonials', href: '/about' },
            { label: 'Pricing', href: '/deals' },
          ],
          ctaText: 'Get Started',
          ctaHref: '/contact',
        },
        fields: [{ key: 'logoText', label: 'Logo text', type: 'text' }, ...NAV_FIELDS, ...CTA_FIELDS],
      },
      {
        id: 'minimal',
        name: 'Minimal',
        description: 'Lightweight bar with an outline button.',
        component: HEADER_VARIATIONS.minimal,
        defaultContent: {
          logoText: '',
          navItems: [
            { label: 'Home', href: '/' },
            { label: 'Vehicles', href: '/vehicles' },
            { label: 'About', href: '/about' },
            { label: 'Contact', href: '/contact' },
          ],
          ctaText: 'Reserve',
          ctaHref: '/vehicles',
          transparent: false,
        },
        fields: [
          { key: 'logoText', label: 'Logo text', type: 'text' },
          ...NAV_FIELDS,
          ...CTA_FIELDS,
          { key: 'transparent', label: 'Transparent background', type: 'boolean' },
        ],
      },
      {
        id: 'two_row',
        name: 'Announcement bar',
        description: 'Thin promo bar above the main navigation.',
        component: HEADER_VARIATIONS.two_row,
        defaultContent: {
          topBar: {
            text: 'Free delivery on orders over 500 DH',
            linkText: 'Shop now',
            linkHref: '/vehicles',
            backgroundColor: '#0D1117',
            textColor: '#ffffff',
          },
          logoText: '',
          navItems: [
            { label: 'Vehicles', href: '/vehicles' },
            { label: 'Categories', href: '/vehicles' },
            { label: 'Deals', href: '/deals' },
            { label: 'Support', href: '/contact' },
          ],
          ctaText: 'Book Vehicle',
          ctaHref: '/vehicles',
        },
        fields: [
          { key: 'topBar.text', label: 'Announcement text', type: 'text' },
          { key: 'topBar.linkText', label: 'Announcement link label', type: 'text' },
          { key: 'topBar.linkHref', label: 'Announcement link', type: 'text' },
          { key: 'topBar.backgroundColor', label: 'Announcement background', type: 'color' },
          { key: 'topBar.textColor', label: 'Announcement text colour', type: 'color' },
          { key: 'logoText', label: 'Logo text', type: 'text' },
          ...NAV_FIELDS,
          ...CTA_FIELDS,
        ],
      },
    ],
  },
  {
    id: 'hero',
    label: 'Hero',
    description: 'The first thing visitors see',
    icon: Sparkles,
    variations: [
      {
        id: 'centered',
        name: 'Centered + search',
        description: 'Full-width image, headline and a booking search bar.',
        component: HERO_VARIATIONS.centered,
        defaultContent: {
          eyebrow: 'Premium car rental in Agadir',
          headline: 'Find your perfect ride',
          subheadline: 'Book a car in minutes. Free cancellation, no hidden fees, delivery to your hotel.',
          ctaText: 'Browse vehicles',
          ctaHref: '/vehicles',
          secondaryCtaText: 'How it works',
          secondaryCtaHref: '/services',
          showSearch: true,
          imageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1600&q=80',
          overlay: 70,
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'secondaryCtaText', label: 'Secondary button label', type: 'text' },
          { key: 'secondaryCtaHref', label: 'Secondary button link', type: 'text' },
          { key: 'showSearch', label: 'Show search bar', type: 'boolean' },
          { key: 'imageUrl', label: 'Background image', type: 'image' },
          { key: 'overlay', label: 'Dark overlay (%)', type: 'number', min: 0, max: 95 },
        ],
      },
      {
        id: 'split',
        name: 'Split image',
        description: 'Text on the left, photo on the right.',
        component: HERO_VARIATIONS.split,
        defaultContent: {
          eyebrow: 'Trusted by 12,000+ travellers',
          headline: 'Drive the coast in style',
          subheadline: 'A modern fleet, transparent prices and roadside assistance included with every booking.',
          ctaText: 'See the fleet',
          ctaHref: '/vehicles',
          secondaryCtaText: 'Talk to us',
          secondaryCtaHref: '/contact',
          imageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1600&q=80',
          bullets: ['Free cancellation up to 24h', 'Unlimited mileage options', '24/7 roadside assistance'],
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'secondaryCtaText', label: 'Secondary button label', type: 'text' },
          { key: 'secondaryCtaHref', label: 'Secondary button link', type: 'text' },
          { key: 'imageUrl', label: 'Image', type: 'image' },
          {
            key: 'bullets',
            label: 'Checklist',
            type: 'repeater',
            stringItems: true,
            itemLabel: 'Point',
          },
        ],
      },
      {
        id: 'full_bleed',
        name: 'Full-bleed overlay',
        description: 'Big photo with copy and stats at the bottom.',
        component: HERO_VARIATIONS.full_bleed,
        defaultContent: {
          headline: 'Your journey starts here',
          subheadline: 'Hand-picked vehicles for every road trip along Morocco’s Atlantic coast.',
          ctaText: 'Book now',
          ctaHref: '/vehicles',
          imageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1600&q=80',
          overlay: 55,
          stats: [
            { value: '120+', label: 'Vehicles' },
            { value: '4.9/5', label: 'Rated by guests' },
            { value: '24/7', label: 'Support' },
          ],
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'imageUrl', label: 'Background image', type: 'image' },
          { key: 'overlay', label: 'Dark overlay (%)', type: 'number', min: 0, max: 95 },
          {
            key: 'stats',
            label: 'Stats',
            type: 'repeater',
            itemLabel: 'Stat',
            itemFields: [
              { key: 'value', label: 'Value', type: 'text' },
              { key: 'label', label: 'Label', type: 'text' },
            ],
          },
        ],
      },
      {
        id: 'minimal',
        name: 'Minimal (no image)',
        description: 'Typography-led hero on a soft background.',
        component: HERO_VARIATIONS.minimal,
        defaultContent: {
          eyebrow: 'Car rental, simplified',
          headline: 'Rent a car in three taps',
          subheadline: 'Pick a vehicle, choose your dates, drive away. No paperwork, no queues.',
          ctaText: 'Start booking',
          ctaHref: '/vehicles',
          secondaryCtaText: 'View prices',
          secondaryCtaHref: '/deals',
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'secondaryCtaText', label: 'Secondary button label', type: 'text' },
          { key: 'secondaryCtaHref', label: 'Secondary button link', type: 'text' },
        ],
      },
      {
        id: 'media',
        name: 'Video / media',
        description: 'Video or poster image with a play affordance.',
        component: HERO_VARIATIONS.media,
        defaultContent: {
          headline: 'See the fleet in motion',
          subheadline: 'A short look at the cars and the service you can expect.',
          videoUrl: '',
          imageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1600&q=80',
          ctaText: 'Book a test drive',
          ctaHref: '/vehicles',
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          { key: 'videoUrl', label: 'Video URL (mp4)', type: 'text', hint: 'Leave empty to use the poster image.' },
          { key: 'imageUrl', label: 'Poster image', type: 'image' },
          ...CTA_FIELDS,
        ],
      },
    ],
  },
  {
    id: 'fleet',
    label: 'Fleet',
    description: 'Live vehicles from the agency fleet',
    icon: Car,
    variations: [
      {
        id: 'featured',
        name: 'Featured fleet',
        description: 'A short grid of highlighted vehicles with prices.',
        component: FLEET_VARIATIONS.featured,
        defaultContent: {
          eyebrow: 'Our fleet',
          headline: 'Cars for every trip',
          subheadline: 'A well-maintained fleet, transparent daily rates and a team that answers.',
          category: 'All',
          limit: 3,
          columns: 3,
          showPrices: true,
          showRating: true,
          ctaText: 'View all vehicles',
          ctaHref: '/vehicles',
          emptyText: 'Vehicles will appear here once the fleet is published.',
        },
        fields: FLEET_FIELDS,
      },
      {
        id: 'full',
        name: 'Full fleet grid',
        description: 'A larger grid for the complete fleet page.',
        component: FLEET_VARIATIONS.full,
        defaultContent: {
          eyebrow: 'The full fleet',
          headline: 'Find your car',
          subheadline: 'Browse every listed vehicle, with transparent daily rates and no surprises.',
          category: 'All',
          limit: 9,
          columns: 3,
          showPrices: true,
          showRating: true,
          ctaText: '',
          ctaHref: '/vehicles',
          emptyText: 'Vehicles will appear here once the fleet is published.',
        },
        fields: FLEET_FIELDS,
      },
    ],
  },
  {
    id: 'features',
    label: 'Features',
    description: 'Why customers should choose you',
    icon: Star,
    variations: [
      {
        id: 'grid',
        name: 'Feature grid',
        description: 'Icon cards in a responsive grid.',
        component: FEATURE_VARIATIONS.grid,
        defaultContent: {
          eyebrow: 'Why book with us',
          headline: 'Everything you need for the road',
          subheadline: 'Simple pricing, a well-maintained fleet and support that actually answers.',
          columns: 4,
          items: [
            { icon: 'shield', title: 'Full insurance included', text: 'Every rental ships with comprehensive cover and a zero-excess option.' },
            { icon: 'clock', title: 'Flexible pick-up', text: 'Collect at the airport, your hotel, or our downtown office — any hour.' },
            { icon: 'card', title: 'No hidden fees', text: 'The price you see at checkout is the price you pay at the counter.' },
            { icon: 'headset', title: '24/7 assistance', text: 'A real person answers the phone, day or night, wherever you are.' },
          ],
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'columns',
            label: 'Columns',
            type: 'select',
            options: [
              { value: '2', label: '2 columns' },
              { value: '3', label: '3 columns' },
              { value: '4', label: '4 columns' },
            ],
          },
          {
            key: 'items',
            label: 'Feature cards',
            type: 'repeater',
            itemLabel: 'Feature',
            itemFields: [
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'text', label: 'Description', type: 'textarea' },
              {
                key: 'icon',
                label: 'Icon',
                type: 'text',
              },
            ],
          },
        ],
      },
      {
        id: 'alternating',
        name: 'Alternating rows',
        description: 'Zig-zag of copy and photos.',
        component: FEATURE_VARIATIONS.alternating,
        defaultContent: {
          headline: 'Built around your trip',
          subheadline: 'Three things our guests tell us made the difference.',
          rows: [
            {
              title: 'Book in under two minutes',
              text: 'Pick your dates, choose a car, pay online. Your keys are waiting when you land.',
              imageUrl: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=80',
            },
            {
              title: 'Cars you can trust',
              text: 'Every vehicle is serviced between rentals and fully cleaned before pick-up.',
              imageUrl: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1200&q=80',
            },
            {
              title: 'Return without the fuss',
              text: 'Drop the keys, we handle the rest. No queue, no surprise inspection fees.',
              imageUrl: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1200&q=80',
            },
          ],
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'rows',
            label: 'Rows',
            type: 'repeater',
            itemLabel: 'Row',
            itemFields: [
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'text', label: 'Text', type: 'textarea' },
              { key: 'imageUrl', label: 'Image', type: 'image' },
            ],
          },
        ],
      },
      {
        id: 'checklist',
        name: 'Checklist + stats',
        description: 'What is included, plus headline numbers.',
        component: FEATURE_VARIATIONS.checklist,
        defaultContent: {
          headline: 'The practical stuff, handled',
          subheadline: 'No fine print surprises. Here is exactly what comes with your booking.',
          items: [
            'Comprehensive insurance with a 500 DH excess',
            'Unlimited kilometres on rentals over 3 days',
            'Free cancellation up to 24 hours before pick-up',
            'Additional driver included at no cost',
            'Child seat available on request',
            'Delivery and collection across Agadir',
          ],
          stats: [
            { value: '120+', label: 'Cars in the fleet' },
            { value: '12k', label: 'Happy guests' },
            { value: '4.9', label: 'Average rating' },
            { value: '8 yrs', label: 'On the road' },
          ],
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          { key: 'items', label: 'Checklist', type: 'repeater', stringItems: true, itemLabel: 'Item' },
          {
            key: 'stats',
            label: 'Stats',
            type: 'repeater',
            itemLabel: 'Stat',
            itemFields: [
              { key: 'value', label: 'Value', type: 'text' },
              { key: 'label', label: 'Label', type: 'text' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'testimonials',
    label: 'Testimonials',
    description: 'Social proof from real customers',
    icon: Star,
    variations: [
      {
        id: 'grid',
        name: 'Review grid',
        description: 'Three review cards with avatars.',
        component: TESTIMONIAL_VARIATIONS.grid,
        defaultContent: {
          eyebrow: 'Guest reviews',
          headline: 'Loved by travellers',
          subheadline: 'A few words from people who drove with us this season.',
          items: [
            {
              name: 'Sara Bennani',
              role: 'Family holiday · Casablanca',
              text: 'The car was spotless and the team delivered it straight to our riad. No queue, no paperwork — exactly what you want after a flight.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=47',
            },
            {
              name: 'James Whitfield',
              role: 'Business trip · London',
              text: 'Booked at 11pm for an 8am pick-up. Confirmation in two minutes and the keys were ready at the airport desk.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=12',
            },
            {
              name: 'Youssef El Amrani',
              role: 'Surf week · Taghazout',
              text: 'Roof racks fitted for free and unlimited kilometres meant we could chase every break down the coast without worrying.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=33',
            },
          ],
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'items',
            label: 'Reviews',
            type: 'repeater',
            itemLabel: 'Review',
            itemFields: [
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'role', label: 'Role / trip', type: 'text' },
              { key: 'text', label: 'Review', type: 'textarea' },
              { key: 'rating', label: 'Rating (1-5)', type: 'number' },
              { key: 'avatar', label: 'Avatar image', type: 'image' },
            ],
          },
        ],
      },
      {
        id: 'featured',
        name: 'Featured quote',
        description: 'One large quote on a dark background.',
        component: TESTIMONIAL_VARIATIONS.featured,
        defaultContent: {
          quote: 'We have rented from every agency in Agadir over the years. This is the first one where the price we booked online was the price we paid at the counter.',
          name: 'Laila Mansouri',
          role: 'Returning guest · Marrakech',
          avatar: 'https://i.pravatar.cc/160?img=45',
          rating: 5,
          stats: [
            { value: '4.9/5', label: 'Average rating' },
            { value: '2,400+', label: 'Reviews' },
            { value: '98%', label: 'Would book again' },
          ],
        },
        fields: [
          { key: 'quote', label: 'Quote', type: 'textarea' },
          { key: 'name', label: 'Name', type: 'text' },
          { key: 'role', label: 'Role / trip', type: 'text' },
          { key: 'avatar', label: 'Avatar image', type: 'image' },
          { key: 'rating', label: 'Rating (1-5)', type: 'number' },
          {
            key: 'stats',
            label: 'Stats',
            type: 'repeater',
            itemLabel: 'Stat',
            itemFields: [
              { key: 'value', label: 'Value', type: 'text' },
              { key: 'label', label: 'Label', type: 'text' },
            ],
          },
        ],
      },
      {
        id: 'compact',
        name: 'Compact list',
        description: 'Score summary beside a list of reviews.',
        component: TESTIMONIAL_VARIATIONS.compact,
        defaultContent: {
          headline: 'What guests say',
          subheadline: 'Verified reviews from recent rentals.',
          items: [
            {
              name: 'Sara Bennani',
              role: 'Family holiday · Casablanca',
              text: 'The car was spotless and the team delivered it straight to our riad.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=47',
            },
            {
              name: 'James Whitfield',
              role: 'Business trip · London',
              text: 'Booked at 11pm for an 8am pick-up. Keys were ready at the airport desk.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=12',
            },
            {
              name: 'Youssef El Amrani',
              role: 'Surf week · Taghazout',
              text: 'Roof racks fitted for free and unlimited kilometres.',
              rating: 5,
              avatar: 'https://i.pravatar.cc/120?img=33',
            },
          ],
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'items',
            label: 'Reviews',
            type: 'repeater',
            itemLabel: 'Review',
            itemFields: [
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'role', label: 'Role / trip', type: 'text' },
              { key: 'text', label: 'Review', type: 'textarea' },
              { key: 'rating', label: 'Rating (1-5)', type: 'number' },
              { key: 'avatar', label: 'Avatar image', type: 'image' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    description: 'Plans, rates and comparisons',
    icon: CreditCard,
    variations: [
      {
        id: 'cards',
        name: 'Three plans',
        description: 'Three tier cards with a highlighted middle plan.',
        component: PRICING_VARIATIONS.cards,
        defaultContent: {
          eyebrow: 'Simple pricing',
          headline: 'Pick the plan that fits',
          subheadline: 'All plans include insurance, unlimited support and free cancellation up to 24 hours.',
          currency: 'DH',
          plans: [
            {
              name: 'Daily',
              price: '350',
              period: '/ day',
              description: 'Short trips and weekend escapes.',
              features: ['1–3 day rentals', '200 km per day included', 'Basic insurance', 'Airport pick-up'],
              ctaText: 'Choose daily',
              ctaHref: '/vehicles',
              highlighted: false,
            },
            {
              name: 'Weekly',
              price: '1,900',
              period: '/ week',
              description: 'The sweet spot for holidays.',
              features: ['4–7 day rentals', 'Unlimited kilometres', 'Full insurance', 'Free delivery in Agadir', 'Additional driver'],
              ctaText: 'Choose weekly',
              ctaHref: '/vehicles',
              highlighted: true,
              badge: 'Most popular',
            },
            {
              name: 'Monthly',
              price: '5,900',
              period: '/ month',
              description: 'Long stays and corporate use.',
              features: ['28+ day rentals', 'Unlimited kilometres', 'Zero-excess insurance', 'Vehicle swap once a month', 'Priority support'],
              ctaText: 'Choose monthly',
              ctaHref: '/vehicles',
              highlighted: false,
            },
          ],
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          { key: 'currency', label: 'Currency suffix', type: 'text' },
          {
            key: 'plans',
            label: 'Plans',
            type: 'repeater',
            itemLabel: 'Plan',
            itemFields: [
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'price', label: 'Price', type: 'text' },
              { key: 'period', label: 'Period', type: 'text' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'ctaText', label: 'Button label', type: 'text' },
              { key: 'ctaHref', label: 'Button link', type: 'text' },
            ],
          },
        ],
      },
      {
        id: 'table',
        name: 'Comparison table',
        description: 'Side-by-side feature comparison.',
        component: PRICING_VARIATIONS.table,
        defaultContent: {
          headline: 'Compare plans',
          subheadline: 'Everything included, side by side.',
          columns: ['Daily', 'Weekly', 'Monthly'],
          rows: [
            { label: 'Minimum rental', values: ['1 day', '4 days', '28 days'] },
            { label: 'Kilometres', values: ['200 / day', 'Unlimited', 'Unlimited'] },
            { label: 'Insurance', values: ['Basic', 'Full', 'Zero excess'] },
            { label: 'Delivery in Agadir', values: ['—', 'Free', 'Free'] },
            { label: 'Additional driver', values: ['30 DH/day', 'Included', 'Included'] },
            { label: 'Vehicle swap', values: ['—', '—', 'Monthly'] },
          ],
          ctaText: 'Book this plan',
          ctaHref: '/vehicles',
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'rows',
            label: 'Rows',
            type: 'repeater',
            itemLabel: 'Row',
            itemFields: [
              { key: 'label', label: 'Feature', type: 'text' },
              { key: 'values', label: 'Values (comma separated)', type: 'text' },
            ],
          },
          ...CTA_FIELDS,
        ],
      },
      {
        id: 'single',
        name: 'Single all-inclusive',
        description: 'One price card with a feature checklist.',
        component: PRICING_VARIATIONS.single,
        defaultContent: {
          eyebrow: 'One price, everything in',
          headline: 'All-inclusive rental',
          price: '450',
          currency: 'DH',
          period: '/ day',
          description: 'Insurance, unlimited support and delivery across Agadir — included in every booking.',
          features: [
            'Comprehensive insurance',
            'Unlimited kilometres',
            'Free delivery and collection',
            'Additional driver at no cost',
            'Free cancellation up to 24h',
            '24/7 roadside assistance',
          ],
          ctaText: 'Book now',
          ctaHref: '/vehicles',
          note: 'No deposit required for online bookings.',
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'price', label: 'Price', type: 'text' },
          { key: 'currency', label: 'Currency', type: 'text' },
          { key: 'period', label: 'Period', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'features', label: 'Included', type: 'repeater', stringItems: true, itemLabel: 'Item' },
          ...CTA_FIELDS,
          { key: 'note', label: 'Small note', type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact',
    description: 'Contact details and a working lead form',
    icon: Mail,
    variations: [
      {
        id: 'split',
        name: 'Split contact',
        description: 'Details on a dark panel and a form beside it.',
        component: CONTACT_VARIATIONS.split,
        defaultContent: { ...CONTACT_DEFAULT },
        fields: CONTACT_FIELDS,
      },
      {
        id: 'card',
        name: 'Centered contact',
        description: 'Centered heading with details and a form below.',
        component: CONTACT_VARIATIONS.card,
        defaultContent: { ...CONTACT_DEFAULT },
        fields: CONTACT_FIELDS,
      },
    ],
  },
  {
    id: 'faq',
    label: 'FAQ',
    description: 'Answers to common customer questions',
    icon: HelpCircle,
    variations: [
      {
        id: 'accordion',
        name: 'Accordion',
        description: 'Compact questions that open one at a time.',
        component: FAQ_VARIATIONS.accordion,
        defaultContent: { ...FAQ_DEFAULT },
        fields: FAQ_FIELDS,
      },
      {
        id: 'cards',
        name: 'Question cards',
        description: 'A two-column grid of questions and answers.',
        component: FAQ_VARIATIONS.cards,
        defaultContent: { ...FAQ_DEFAULT },
        fields: FAQ_FIELDS,
      },
    ],
  },
  {
    id: 'gallery',
    label: 'Gallery',
    description: 'Photo grids and mosaics',
    icon: ImageIcon,
    variations: [
      {
        id: 'grid',
        name: 'Photo grid',
        description: 'A clean grid of photos with optional captions.',
        component: GALLERY_VARIATIONS.grid,
        defaultContent: { ...GALLERY_DEFAULT },
        fields: GALLERY_FIELDS,
      },
      {
        id: 'mosaic',
        name: 'Mosaic',
        description: 'One large photo with four smaller ones.',
        component: GALLERY_VARIATIONS.mosaic,
        defaultContent: { ...GALLERY_DEFAULT },
        fields: GALLERY_FIELDS,
      },
    ],
  },
  {
    id: 'steps',
    label: 'Steps',
    description: 'Explain how the rental process works',
    icon: ListOrdered,
    variations: [
      {
        id: 'numbered',
        name: 'Numbered steps',
        description: 'A simple numbered process in four columns.',
        component: STEPS_VARIATIONS.numbered,
        defaultContent: { ...STEPS_DEFAULT },
        fields: STEP_FIELDS,
      },
      {
        id: 'cards',
        name: 'Step cards',
        description: 'A two-column card layout for each step.',
        component: STEPS_VARIATIONS.cards,
        defaultContent: { ...STEPS_DEFAULT },
        fields: STEP_FIELDS,
      },
    ],
  },
  {
    id: 'stats',
    label: 'Stats',
    description: 'Numbers and proof points',
    icon: BarChart3,
    variations: [
      {
        id: 'band',
        name: 'Dark stat band',
        description: 'Four large numbers on a dark background.',
        component: STATS_VARIATIONS.band,
        defaultContent: { ...STATS_DEFAULT },
        fields: STAT_FIELDS,
      },
      {
        id: 'inline',
        name: 'Light stat cards',
        description: 'Four stat cards on a light background.',
        component: STATS_VARIATIONS.inline,
        defaultContent: { ...STATS_DEFAULT },
        fields: STAT_FIELDS,
      },
    ],
  },
  {
    id: 'location',
    label: 'Location',
    description: 'Address, hours and an embedded map',
    icon: MapPin,
    variations: [
      {
        id: 'map',
        name: 'Map and details',
        description: 'Contact details beside an embedded map.',
        component: LOCATION_VARIATIONS.map,
        defaultContent: { ...LOCATION_DEFAULT },
        fields: LOCATION_FIELDS,
      },
      {
        id: 'details',
        name: 'Details card',
        description: 'A single card with address, phone and hours.',
        component: LOCATION_VARIATIONS.details,
        defaultContent: { ...LOCATION_DEFAULT },
        fields: LOCATION_FIELDS,
      },
    ],
  },
  {
    id: 'cta',
    label: 'Call to action',
    description: 'Push visitors toward booking',
    icon: Megaphone,
    variations: [
      {
        id: 'banner',
        name: 'Dark banner',
        description: 'Centered call to action on a dark band.',
        component: CTA_VARIATIONS.banner,
        defaultContent: {
          headline: 'Ready to hit the road?',
          subheadline: 'Book today and pick up tomorrow. Free cancellation up to 24 hours before your rental.',
          ctaText: 'Book a vehicle',
          ctaHref: '/vehicles',
          secondaryCtaText: 'Call us',
          secondaryCtaHref: 'tel:+212528840000',
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'secondaryCtaText', label: 'Secondary button label', type: 'text' },
          { key: 'secondaryCtaHref', label: 'Secondary button link', type: 'text' },
        ],
      },
      {
        id: 'split',
        name: 'Split with photo',
        description: 'Copy beside a photo in a rounded card.',
        component: CTA_VARIATIONS.split,
        defaultContent: {
          eyebrow: 'Limited availability',
          headline: 'Summer rates are live',
          subheadline: 'Lock in your dates now — prices go up in July. Free changes until 48 hours before pick-up.',
          ctaText: 'See summer deals',
          ctaHref: '/deals',
          imageUrl: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1400&q=80',
        },
        fields: [
          { key: 'eyebrow', label: 'Small label above title', type: 'text' },
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          ...CTA_FIELDS,
          { key: 'imageUrl', label: 'Image', type: 'image' },
        ],
      },
      {
        id: 'cards',
        name: 'Two actions',
        description: 'Book online or talk to the team.',
        component: CTA_VARIATIONS.cards,
        defaultContent: {
          headline: 'Two ways to get on the road',
          subheadline: 'Book online in minutes, or talk to the team if you need something specific.',
          cards: [
            {
              icon: 'car',
              title: 'Book online',
              text: 'Choose your car, pick your dates and pay securely. Instant confirmation.',
              ctaText: 'Browse the fleet',
              ctaHref: '/vehicles',
            },
            {
              icon: 'phone',
              title: 'Talk to us',
              text: 'Long-term rental, corporate account or a special request? We answer 7 days a week.',
              ctaText: 'Call the office',
              ctaHref: 'tel:+212528840000',
            },
          ],
        },
        fields: [
          { key: 'headline', label: 'Headline', type: 'textarea' },
          { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
          {
            key: 'cards',
            label: 'Action cards',
            type: 'repeater',
            itemLabel: 'Card',
            itemFields: [
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'text', label: 'Text', type: 'textarea' },
              { key: 'ctaText', label: 'Button label', type: 'text' },
              { key: 'ctaHref', label: 'Button link', type: 'text' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'footer',
    label: 'Footer',
    description: 'Bottom of the page, links and legal',
    icon: PanelBottom,
    variations: [
      {
        id: 'columns',
        name: 'Multi-column',
        description: 'Contact block plus three link columns.',
        component: FOOTER_VARIATIONS.columns,
        defaultContent: {
          logoText: '',
          description: 'Car rental along Morocco’s Atlantic coast. A modern fleet, honest prices and people who pick up the phone.',
          columns: [
            {
              title: 'Company',
              links: [
                { label: 'About us', href: '/about' },
                { label: 'Careers', href: '/about' },
                { label: 'Press', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ],
            },
            {
              title: 'Rentals',
              links: [
                { label: 'Our fleet', href: '/vehicles' },
                { label: 'Long-term rental', href: '/vehicles' },
                { label: 'Corporate accounts', href: '/contact' },
                { label: 'Airport pick-up', href: '/locations' },
              ],
            },
            {
              title: 'Support',
              links: [
                { label: 'Help centre', href: '/faq' },
                { label: 'Rental terms', href: '/legal/terms' },
                { label: 'Insurance', href: '/faq' },
                { label: 'FAQ', href: '/faq' },
              ],
            },
          ],
          phone: '',
          email: '',
          address: '',
          legal: '',
          socials: [
            { network: 'facebook', href: 'https://facebook.com' },
            { network: 'instagram', href: 'https://instagram.com' },
            { network: 'linkedin', href: 'https://linkedin.com' },
          ],
        },
        fields: [
          { key: 'logoText', label: 'Logo text', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          {
            key: 'columns',
            label: 'Link columns',
            type: 'repeater',
            itemLabel: 'Column',
            itemFields: [
              { key: 'title', label: 'Column title', type: 'text' },
              {
                key: 'links',
                label: 'Links (Label|/href per line)',
                type: 'textarea',
              },
            ],
          },
          { key: 'phone', label: 'Phone', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
          { key: 'address', label: 'Address', type: 'textarea' },
          { key: 'legal', label: 'Legal line', type: 'text' },
        ],
      },
      {
        id: 'simple',
        name: 'Simple centered',
        description: 'Compact footer with a single link row.',
        component: FOOTER_VARIATIONS.simple,
        defaultContent: {
          logoText: '',
          tagline: 'Rentals made simple.',
          links: [
            { label: 'Vehicles', href: '/vehicles' },
            { label: 'Pricing', href: '/deals' },
            { label: 'Contact', href: '/contact' },
            { label: 'Terms', href: '/legal/terms' },
            { label: 'Privacy', href: '/legal/privacy' },
          ],
          legal: '',
        },
        fields: [
          { key: 'logoText', label: 'Logo text', type: 'text' },
          { key: 'tagline', label: 'Tagline', type: 'text' },
          {
            key: 'links',
            label: 'Links',
            type: 'repeater',
            itemLabel: 'Link',
            itemFields: [
              { key: 'label', label: 'Label', type: 'text' },
              { key: 'href', label: 'Link', type: 'text' },
            ],
          },
          { key: 'legal', label: 'Legal line', type: 'text' },
        ],
      },
      {
        id: 'newsletter',
        name: 'With newsletter',
        description: 'Signup strip above the link columns.',
        component: FOOTER_VARIATIONS.newsletter,
        defaultContent: {
          logoText: '',
          newsletterTitle: 'Deals in your inbox',
          newsletterText: 'One short email a month with seasonal rates and last-minute availability. No spam.',
          placeholder: 'your@email.com',
          buttonText: 'Subscribe',
          newsletterSuccess: 'You are subscribed. Watch your inbox.',
          columns: [
            {
              title: 'Company',
              links: [
                { label: 'About us', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ],
            },
            {
              title: 'Rentals',
              links: [
                { label: 'Our fleet', href: '/vehicles' },
                { label: 'Long-term', href: '/vehicles' },
              ],
            },
            {
              title: 'Support',
              links: [
                { label: 'Help centre', href: '/faq' },
                { label: 'Terms', href: '/legal/terms' },
              ],
            },
          ],
          legal: '',
        },
        fields: [
          { key: 'logoText', label: 'Logo text', type: 'text' },
          { key: 'newsletterTitle', label: 'Newsletter title', type: 'text' },
          { key: 'newsletterText', label: 'Newsletter text', type: 'textarea' },
          { key: 'placeholder', label: 'Email placeholder', type: 'text' },
          { key: 'buttonText', label: 'Subscribe button label', type: 'text' },
          { key: 'newsletterSuccess', label: 'Success message', type: 'text' },
          { key: 'legal', label: 'Legal line', type: 'text' },
        ],
      },
    ],
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Imported HTML and CSS sections',
    icon: Code2,
    variations: CUSTOM_VARIATIONS,
  },
];

// ──────────────────────────────────────────────────────────────
// Lookups
// ──────────────────────────────────────────────────────────────

export function getCategory(category: SectionCategory): CategoryDef | undefined {
  return SECTION_LIBRARY.find((c) => c.id === category);
}

export function getVariation(category: SectionCategory, variationId: string): VariationDef | undefined {
  return getCategory(category)?.variations.find((v) => v.id === variationId);
}

export function getSectionComponent(category: SectionCategory, variationId: string) {
  return getVariation(category, variationId)?.component ?? null;
}

// ──────────────────────────────────────────────────────────────
// Page model used by the new builder
// ──────────────────────────────────────────────────────────────

export interface PageSection {
  id: string;
  category: SectionCategory;
  variationId: string;
  content: Record<string, any>;
  style: SectionStyle;
}

export interface BuilderPage {
  name: string;
  sections: PageSection[];
  settings: PageSettings;
  designTokens: PageDesignTokens;
  updatedAt: string;
}

export function makeSection(
  category: SectionCategory,
  variationId: string,
  agency?: AgencyBrandProfile | null,
): PageSection | null {
  const variation = getVariation(category, variationId);
  if (!variation) return null;
  const content = applyAgencyBrandToContent(
    JSON.parse(JSON.stringify(variation.defaultContent)) as Record<string, unknown>,
    agency,
  );
  return {
    id: createSectionId(),
    category,
    variationId,
    content,
    style: { ...DEFAULT_SECTION_STYLE },
  };
}

/** A complete starter page — header, hero, features, reviews, CTA, footer. */
export type PageTemplate = 'home' | 'fleet' | 'contact' | 'about' | 'faq' | 'gallery' | 'blank';

export interface PageTemplateDef {
  id: PageTemplate;
  label: string;
  description: string;
  sections: [SectionCategory, string][];
}

export const PAGE_TEMPLATES: PageTemplateDef[] = [
  {
    id: 'home',
    label: 'Home page',
    description: 'Hero, live fleet, benefits, reviews, stats and a call to action.',
    sections: [
      ['header', 'classic'],
      ['hero', 'centered'],
      ['fleet', 'featured'],
      ['features', 'grid'],
      ['testimonials', 'grid'],
      ['stats', 'band'],
      ['cta', 'banner'],
      ['footer', 'columns'],
    ],
  },
  {
    id: 'fleet',
    label: 'Fleet page',
    description: 'A full fleet grid with the rental steps and a booking call to action.',
    sections: [
      ['hero', 'split'],
      ['fleet', 'full'],
      ['steps', 'numbered'],
      ['cta', 'banner'],
    ],
  },
  {
    id: 'contact',
    label: 'Contact page',
    description: 'A working contact form, location map and common questions.',
    sections: [
      ['hero', 'centered'],
      ['contact', 'split'],
      ['location', 'map'],
      ['faq', 'accordion'],
    ],
  },
  {
    id: 'about',
    label: 'About page',
    description: 'Your story, benefits, proof points and customer reviews.',
    sections: [
      ['hero', 'split'],
      ['features', 'grid'],
      ['stats', 'band'],
      ['testimonials', 'grid'],
      ['cta', 'banner'],
    ],
  },
  {
    id: 'faq',
    label: 'FAQ page',
    description: 'A dedicated questions page with a contact card.',
    sections: [
      ['hero', 'centered'],
      ['faq', 'accordion'],
      ['contact', 'card'],
    ],
  },
  {
    id: 'gallery',
    label: 'Gallery page',
    description: 'A photo-led page with a gallery and a call to action.',
    sections: [
      ['hero', 'media'],
      ['gallery', 'grid'],
      ['cta', 'banner'],
    ],
  },
  {
    id: 'blank',
    label: 'Blank page',
    description: 'An empty page — add the sections you need.',
    sections: [],
  },
];

export function createPageFromTemplate(
  template: PageTemplate,
  name = 'Home',
  agency?: AgencyBrandProfile | string | null,
): BuilderPage {
  const brand: AgencyBrandProfile | null = typeof agency === 'string'
    ? { name: agency }
    : (agency ?? null);
  const logoText = brand?.name?.trim() || name;
  const definition = PAGE_TEMPLATES.find((item) => item.id === template) ?? PAGE_TEMPLATES[0];
  const sections = definition.sections
    .map(([category, variationId]) => makeSection(category, variationId, brand))
    .filter((section): section is PageSection => section !== null);

  for (const section of sections) {
    if (section.category === 'header' || section.category === 'footer') {
      section.content = applyAgencyBrandToContent(
        { ...section.content, logoText: section.content.logoText || logoText },
        brand,
      );
    }
  }

  return {
    name,
    sections,
    settings: {},
    designTokens: {},
    updatedAt: new Date().toISOString(),
  };
}

/** A complete home page — header, hero, live fleet, benefits, reviews, stats, CTA, footer. */
export function createStarterPage(
  name = 'Home',
  agency?: AgencyBrandProfile | string | null,
): BuilderPage {
  return createPageFromTemplate('home', name, agency);
}

/** Read/write a dot-path key such as `topBar.text` */
export function getByPath(obj: any, path: string): any {
  return path.split('.').reduce((acc: any, part) => (acc == null ? acc : acc[part]), obj);
}

export function setByPath(obj: any, path: string, value: any): any {
  const parts = path.split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor: any = clone;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i];
    cursor[part] = cursor[part] == null
      ? {}
      : Array.isArray(cursor[part])
        ? [...cursor[part]]
        : { ...cursor[part] };
    cursor = cursor[part];
  }
  cursor[parts[parts.length - 1]] = value;
  return clone;
}

export function isValidSection(value: any): value is PageSection {
  return (
    value &&
    typeof value === 'object' &&
    typeof value.id === 'string' &&
    typeof value.category === 'string' &&
    typeof value.variationId === 'string' &&
    typeof value.content === 'object'
  );
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeBuilderPage(raw: any): BuilderPage | null {
  if (!raw || typeof raw !== 'object') return null;
  const sections = Array.isArray(raw.sections) ? raw.sections.filter(isValidSection) : [];
  return {
    name: typeof raw.name === 'string' ? raw.name : 'Home',
    sections: sections.map((s: PageSection) => ({
      ...s,
      id: s.id || createSectionId(),
      style: { ...DEFAULT_SECTION_STYLE, ...(s.style ?? {}) },
    })),
    settings: isRecord(raw.settings) ? raw.settings : {},
    designTokens: isRecord(raw.designTokens)
      ? raw.designTokens
      : isRecord(raw.design_tokens)
        ? raw.design_tokens
        : {},
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  };
}
